/**
 * Prescriber signing page — the Model A review-and-sign leg (/bridge/prescriber).
 *
 * A referring physician gets a PHI-free SMS with a magic link. This page is
 * what it opens:
 *
 *   PIN     — the signing-PIN gate (server-verified; see PinChallenge).
 *   REVIEW  — the finalized form, patient portion included, rendered page by
 *             page. The physician signs LAST so their attestation covers the
 *             complete document. This IS the review the Provider Services
 *             Agreement's responsibility clause refers to.
 *   SIGN    — the responsibility statement in one line, a typed-cursive
 *             signature, and today's date; submitted sealed to the Mac.
 *
 * Reviewing and signing are one act. The submission carries `served_pdf_sha256`
 * — a hash of the exact PDF bytes this page decrypted and displayed — so "the
 * physician signed THIS version" is checkable against the hash the Mac recorded
 * at dispatch rather than merely asserted.
 *
 * Design: docs/pilot/MODEL_A_PILOT_PLAN.md §3.3, §4.6, §8.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
// Bundled worker (Vite ?url asset), legacy build for the es2015 target — same
// choice as BridgeFieldsForm: no runtime CDN in a PHI-bearing page, and the
// worker always version-matches pdfjs-dist.
import pdfjsWorker from "pdfjs-dist/legacy/build/pdf.worker.min.mjs?url";

import PinChallenge from "./PinChallenge";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import {
  clearBridgeSession,
  getBridgeSession,
  openSecretBox,
  sealSubmission,
  sha256HexBytes,
  submitSealedToBridge,
} from "../../lib/bridgeClient";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

/** In production the bridge serves this SPA, so "" (same-origin) is correct. */
const BRIDGE_BASE_URL = (import.meta.env.VITE_BRIDGE_URL || "").replace(/\/+$/, "");

const PDF_PAGE_MAX_WIDTH = 760;

/**
 * The responsibility statement, verbatim from the Provider Services Agreement
 * (MODEL_A_PILOT_PLAN.md §3.1 / §4.6). It is submitted with the signature as
 * `attestation.text`, so the record shows exactly what was on screen — do not
 * reword it here without changing the agreement.
 */
const ATTESTATION_TEXT =
  "By signing you accept responsibility for the validity of this form's statements and the integrity of its information";

/** Cursive faces offered for the typed signature — same set as the patient leg. */
const SIGNATURE_FONTS = [
  { name: "Dancing Script", label: "Elegant" },
  { name: "Great Vibes", label: "Classic" },
  { name: "Sacramento", label: "Flowing" },
  { name: "Allura", label: "Formal" },
];

type Session = ReturnType<typeof getBridgeSession>;

const STAGE = {
  PIN: "pin",
  REVIEW: "review",
  SIGN: "sign",
  SUBMITTING: "submitting",
  DONE: "done",
  ERROR: "error",
};

/**
 * Where the PIN gate hands off.
 *
 * DEFERRED — screen 1 of §4.6, a short questionnaire of **provider-attestation
 * items** (counseling attestations, supervision statements, clinical-judgment
 * checkboxes — things no chart ever contained), rendered by the shared
 * SurveyEngine in physician mode, belongs HERE: a `STAGE.ITEMS` between PIN and
 * REVIEW, with its answers merged into `field_values` at submit. It is not
 * built because its item source does not exist yet — nothing classifies a
 * form's questions as provider-attestation, so there is no list to render, and
 * guessing one would put chart-derivable gaps in front of the physician days
 * later and out of context. Those belong to the §4.4 pre-flight bounce, never
 * to the signing link. When the classifier lands, insert the stage and point
 * this constant at it; nothing else here has to move.
 */
const STAGE_AFTER_PIN = STAGE.REVIEW;

/** Today, as the local calendar date the physician is signing on. */
function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Rasterize a typed signature to a PNG data URL in the chosen cursive face.
 * 3x scale for crisp stamping into the PDF signature box; ink-blue to read as
 * a signature. A local copy of BridgeFieldsForm's helper — the patient form is
 * a 2000-line component and importing from it would drag the whole patient
 * wizard into this page's bundle.
 */
function typedSignatureToPng(name: string, font: string): string | null {
  if (!name) return null;
  try {
    const scale = 3;
    const fontPx = 44;
    const probe = document.createElement("canvas").getContext("2d") as CanvasRenderingContext2D;
    probe.font = `${fontPx}px '${font}', cursive`;
    const textW = Math.ceil(probe.measureText(name).width);
    const canvas = document.createElement("canvas");
    canvas.width = (textW + 32) * scale;
    canvas.height = Math.ceil(fontPx * 1.6) * scale;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
    ctx.scale(scale, scale);
    ctx.font = `${fontPx}px '${font}', cursive`;
    ctx.fillStyle = "#1a2a6e";
    ctx.textBaseline = "middle";
    ctx.fillText(name, 16, (fontPx * 1.6) / 2);
    return canvas.toDataURL("image/png");
  } catch {
    return null; // fall back to the typed-name path server-side
  }
}

export default function BridgePrescriberPage() {
  // Held in state, not a mount-time memo: passing the PIN REPLACES the stored
  // session token with a verified one, and every fetch below needs the new one.
  const [session, setSession] = useState<Session>(() => getBridgeSession());
  const requiresPin = !!session?.identityRequired;
  const [stage, setStage] = useState(requiresPin ? STAGE.PIN : STAGE_AFTER_PIN);

  const [pdfDoc, setPdfDoc] = useState<any>(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  /** sha256 of the decrypted PDF BYTES — the non-repudiation anchor. */
  const [servedPdfSha256, setServedPdfSha256] = useState<string | null>(null);

  const [signatureName, setSignatureName] = useState("");
  const [signatureFont, setSignatureFont] = useState(SIGNATURE_FONTS[0].name);
  const [error, setError] = useState<string | null>(null);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // Callback ref so the page render re-fires when the canvas actually ATTACHES
  // — it mounts a stage later than the document loads, and a pure dep list
  // misses that, leaving page 1 blank until "Next" is clicked.
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const attachCanvas = useCallback((el: HTMLCanvasElement | null) => {
    canvasRef.current = el;
    setCanvasEl(el);
  }, []);

  // Deliberately NOT `session.formTitle`. Redeem returns a generic title to
  // every role now ("Insurance coverage request…"), because redeem happens
  // BEFORE the identity challenge and the real title names the medication
  // (bridge/routers/magic.py). Rendering it here would put a deliberately
  // neutered string where a form name looks like it should be; the document
  // itself names the drug on screen, one stage later, to a verified reader.
  const PAGE_TITLE = "Review and sign";

  // Which canonical fields this signature writes. A name containing "date"
  // is the date line; everything else is a signature line. Both lists are
  // filled — a form can carry more than one prescriber signature block.
  const fieldNames = useMemo(() => {
    const raw = session?.fieldNames;
    return Array.isArray(raw)
      ? raw.filter((n): n is string => typeof n === "string" && n.length > 0)
      : [];
  }, [session]);
  const dateFieldNames = useMemo(
    () => fieldNames.filter((n) => /date/i.test(n)),
    [fieldNames]
  );
  const signatureFieldNames = useMemo(
    () => fieldNames.filter((n) => !/date/i.test(n)),
    [fieldNames]
  );

  // Fetch + decrypt + render the completed form, once, after the PIN passes.
  // The prescriber slot serves the FULL form; there is no fields-schema or
  // profile behind it, so this is the only content fetch on this page.
  useEffect(() => {
    if (stage !== STAGE.REVIEW) return;
    if (pdfDoc || pdfError) return;
    let cancelled = false;
    (async () => {
      const s = getBridgeSession();
      if (!s?.sessionToken || !s?.assignmentId || !s?.symmetricKeyB64) {
        setPdfError(
          "This link is missing the key that opens the form. Please tap the link in the original text message rather than a copied address."
        );
        return;
      }
      try {
        const resp = await fetch(
          `${BRIDGE_BASE_URL}/api/bridge/assignments/${encodeURIComponent(
            s.assignmentId
          )}/pdf`,
          {
            method: "GET",
            headers: { "X-Patient-Token": s.sessionToken },
            credentials: "omit",
          }
        );
        if (!resp.ok) throw new Error(`pdf fetch failed (${resp.status})`);
        const body = await resp.json();
        if (!body?.encrypted_b64) throw new Error("no document on this assignment");
        const pdfBytes = openSecretBox(body.encrypted_b64, s.symmetricKeyB64);
        // Fingerprint the bytes BEFORE anything renders them: this is what the
        // signature is bound to.
        const fingerprint = await sha256HexBytes(pdfBytes);
        if (cancelled) return;
        setServedPdfSha256(fingerprint);
        const url = URL.createObjectURL(
          new Blob([pdfBytes as BlobPart], { type: "application/pdf" })
        );
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        setPdfBlobUrl(url);
        const doc = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        setPdfDoc(doc);
        setNumPages(doc.numPages);
        setCurrentPage(1);
      } catch {
        if (cancelled) return;
        // Deliberately generic: this page's failures must not narrate anything
        // about the patient or the document.
        setPdfError(
          "We couldn't open this form. Please try again in a moment, or contact Parseo Health for a fresh link."
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [stage, pdfDoc, pdfError]);

  // Render the current page whenever the doc, the page, or the canvas changes.
  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return;
    let cancelled = false;
    (async () => {
      const page = await pdfDoc.getPage(currentPage);
      if (cancelled) return;
      const viewport0 = page.getViewport({ scale: 1 });
      const scale = Math.min(PDF_PAGE_MAX_WIDTH / viewport0.width, 2.0);
      const viewport = page.getViewport({ scale });
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext("2d");
      await page.render({ canvasContext: ctx, viewport }).promise;
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfDoc, currentPage, canvasEl]);

  // Free the Blob URL on unmount.
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  async function handleSign() {
    const legalName = signatureName.trim();
    if (!legalName) return;
    setStage(STAGE.SUBMITTING);
    try {
      const s = getBridgeSession();
      if (!s?.pubkeyB64 || !s?.assignmentId) {
        throw new Error("signing session lost — please re-open the link");
      }
      if (!servedPdfSha256) {
        // Should be unreachable: SIGN is only reachable once the document has
        // rendered. Refuse rather than submit a signature with no anchor.
        throw new Error("the reviewed form's fingerprint is missing");
      }
      const signedAt = new Date().toISOString();
      const signedDate = todayIso();

      const fieldValues: Record<string, string> = {};
      for (const name of signatureFieldNames) fieldValues[name] = legalName;
      for (const name of dateFieldNames) fieldValues[name] = signedDate;

      // No patient_id, no profile, no consent, no update_my_file: the Mac
      // resolves the patient from its own record, and a prescriber must never
      // write to a patient's file.
      const submission = {
        form_id: (s.formId as string | undefined) ?? null,
        field_values: fieldValues,
        signature: {
          authorization_signature: legalName,
          signature_font: signatureFont,
          signature_image_b64: typedSignatureToPng(legalName, signatureFont),
          signed_at: signedAt,
          signature_source: "prescriber_typed",
        },
        signer_role: "prescriber",
        served_pdf_sha256: servedPdfSha256,
        attestation: { text: ATTESTATION_TEXT, accepted_at: signedAt },
        submitted_at: signedAt,
      };

      const ciphertextB64 = sealSubmission(submission, s.pubkeyB64 as string);
      await submitSealedToBridge({
        assignmentId: s.assignmentId as string,
        ciphertextB64,
      });
      clearBridgeSession();
      setStage(STAGE.DONE);
    } catch (err) {
      setError((err as Error).message || String(err));
      setStage(STAGE.ERROR);
    }
  }

  // No bridge session at all → back to the root.
  if (!session) {
    return <Navigate to="/" replace />;
  }
  // A patient / plan-member / legacy link that somehow landed here belongs in
  // the patient wizard.
  if (session.role !== "prescriber") {
    return <Navigate to="/bridge/onboard" replace />;
  }

  if (stage === STAGE.PIN) {
    return (
      <PinChallenge
        onPass={() => {
          // Re-read: verifyIdentity swapped the stored token for a verified one.
          setSession(getBridgeSession());
          setStage(STAGE_AFTER_PIN);
        }}
        onFail={() => {
          // PinChallenge has already cleared the session; it keeps the
          // lockout message on screen.
        }}
      />
    );
  }

  if (stage === STAGE.REVIEW) {
    return (
      <Shell title={PAGE_TITLE} subtitle="Check the completed form, then sign it below.">
        {pdfError ? (
          <p style={{ color: "#c25450", lineHeight: 1.5, margin: 0 }}>{pdfError}</p>
        ) : !pdfDoc ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <ParseoLogo animated size={64} />
            <p style={{ color: "#5b6e7c", marginTop: 14 }}>Opening the form…</p>
          </div>
        ) : (
          <>
            <div style={{ textAlign: "center" }}>
              <canvas
                ref={attachCanvas}
                style={{
                  maxWidth: "100%",
                  display: "block",
                  margin: "0 auto",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  background: "white",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                margin: "14px 0 4px",
                color: "#5b6e7c",
              }}
            >
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                style={pagerButtonStyle(currentPage <= 1)}
              >
                ‹ Previous
              </button>
              <span style={{ fontSize: 14 }}>
                Page {currentPage} of {numPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= numPages}
                onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
                style={pagerButtonStyle(currentPage >= numPages)}
              >
                Next ›
              </button>
            </div>
            <button
              type="button"
              onClick={() => setStage(STAGE.SIGN)}
              style={{
                width: "100%",
                marginTop: 18,
                padding: "13px 16px",
                fontSize: 16,
                fontWeight: 600,
                border: "none",
                borderRadius: 10,
                backgroundColor: "#1b5e6e",
                color: "white",
                cursor: "pointer",
              }}
            >
              Continue to sign
            </button>
          </>
        )}
      </Shell>
    );
  }

  if (stage === STAGE.SIGN) {
    const nameReady = signatureName.trim().length > 0;
    return (
      <Shell title={PAGE_TITLE} subtitle="Attestation and signature.">
        {/* The cursive faces below are Google-hosted, same as the patient
            signature step. */}
        <link
          href="https://fonts.googleapis.com/css2?family=Dancing+Script&family=Great+Vibes&family=Sacramento&family=Allura&display=swap"
          rel="stylesheet"
        />
        <div
          style={{
            padding: 16,
            borderRadius: 10,
            border: "1px solid #e6dccb",
            backgroundColor: "#f5f0eb",
            color: "#182a33",
            fontSize: 15,
            lineHeight: 1.55,
            marginBottom: 20,
          }}
        >
          {ATTESTATION_TEXT}.
        </div>

        <label
          htmlFor="prescriber-signature-name"
          style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#182a33", marginBottom: 6 }}
        >
          Your full name, as it should appear
        </label>
        <input
          id="prescriber-signature-name"
          type="text"
          aria-label="Your full name, as it should appear"
          value={signatureName}
          placeholder="Type your full name"
          autoComplete="name"
          onChange={(e) => setSignatureName(e.target.value)}
          style={{
            width: "100%",
            padding: "11px 13px",
            fontSize: 16,
            border: "1px solid #e6dccb",
            borderRadius: 8,
            boxSizing: "border-box",
            color: "#182a33",
            marginBottom: 16,
          }}
        />

        <div style={{ fontSize: 13, fontWeight: 600, color: "#182a33", marginBottom: 8 }}>
          Choose a style
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 10,
            marginBottom: 18,
          }}
        >
          {SIGNATURE_FONTS.map((font) => (
            <button
              key={font.name}
              type="button"
              onClick={() => setSignatureFont(font.name)}
              style={{
                padding: "14px 12px",
                borderRadius: 10,
                cursor: "pointer",
                border:
                  signatureFont === font.name
                    ? "2px solid #1b5e6e"
                    : "2px solid #e6dccb",
                backgroundColor: signatureFont === font.name ? "#e4ecee" : "white",
                textAlign: "center",
              }}
            >
              <div
                style={{
                  fontFamily: `'${font.name}', cursive`,
                  fontSize: 22,
                  color: "#182a33",
                  marginBottom: 4,
                }}
              >
                {signatureName.trim() || "Your Name"}
              </div>
              <div style={{ fontSize: 11, color: "#5b6e7c" }}>{font.label}</div>
            </button>
          ))}
        </div>

        <div
          style={{
            padding: "18px 12px",
            borderRadius: 8,
            border: "1px dashed #e6dccb",
            backgroundColor: "#f5f0eb",
            textAlign: "center",
            fontFamily: `'${signatureFont}', cursive`,
            fontSize: 30,
            color: "#182a33",
            minHeight: 60,
            marginBottom: 14,
          }}
        >
          {signatureName.trim() || (
            <span style={{ fontFamily: "system-ui", fontSize: 13, color: "#a4a4a4" }}>
              Your signature preview will appear here
            </span>
          )}
        </div>

        <label
          htmlFor="prescriber-signature-date"
          style={{ display: "block", fontSize: 13, fontWeight: 600, color: "#182a33", marginBottom: 6 }}
        >
          Date
        </label>
        <input
          id="prescriber-signature-date"
          type="text"
          readOnly
          aria-label="Date"
          value={todayIso()}
          style={{
            width: "100%",
            padding: "11px 13px",
            fontSize: 15,
            border: "1px solid #e6dccb",
            borderRadius: 8,
            boxSizing: "border-box",
            backgroundColor: "#f3f3f3",
            color: "#5b6e7c",
            marginBottom: 18,
          }}
        />

        <button
          type="button"
          disabled={!nameReady}
          onClick={handleSign}
          style={{
            width: "100%",
            padding: "13px 16px",
            fontSize: 16,
            fontWeight: 600,
            border: "none",
            borderRadius: 10,
            backgroundColor: nameReady ? "#1b5e6e" : "#bbb",
            color: "white",
            cursor: nameReady ? "pointer" : "not-allowed",
          }}
        >
          Sign and submit
        </button>
        <button
          type="button"
          onClick={() => setStage(STAGE.REVIEW)}
          style={{
            display: "block",
            margin: "12px auto 0",
            background: "none",
            border: "none",
            color: "#1b5e6e",
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          ‹ Back to the form
        </button>
      </Shell>
    );
  }

  if (stage === STAGE.SUBMITTING) {
    return centered(
      <>
        <ParseoLogo animated size={72} />
        <h2 style={{ color: "#182a33", margin: "16px 0 6px" }}>Recording your signature…</h2>
        <p style={{ color: "#5b6e7c", margin: 0, maxWidth: 420 }}>
          Encrypting and submitting securely.
        </p>
      </>
    );
  }

  if (stage === STAGE.DONE) {
    return centered(
      <>
        <ParseoLogo size={56} />
        <h2 style={{ color: "#182a33", margin: "16px 0 4px" }}>
          Thank you — your signature has been recorded.
        </h2>
        <p style={{ color: "#5b6e7c", maxWidth: 440, lineHeight: 1.5, margin: 0 }}>
          Parseo will submit this to the insurer.
        </p>
        <p style={{ fontSize: 12.5, color: "#5b6e7c", marginTop: 16, maxWidth: 440, lineHeight: 1.55 }}>
          You can close this page. We'll let you know when the insurer responds.
        </p>
      </>
    );
  }

  // STAGE.ERROR
  return centered(
    <>
      <h2 style={{ color: "#c25450", marginBottom: 8 }}>Signature not recorded</h2>
      <p style={{ color: "#5b6e7c", maxWidth: 420, lineHeight: 1.5 }}>
        Something went wrong sending your signature. Nothing has been submitted
        to the insurer. Please try the link again, or contact Parseo Health.
      </p>
      {error && (
        <p style={{ color: "#999", fontSize: 12, maxWidth: 420 }}>Detail: {error}</p>
      )}
    </>
  );
}

function pagerButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "7px 14px",
    borderRadius: 8,
    border: "1px solid #e6dccb",
    background: "white",
    color: disabled ? "#b9b9b9" : "#182a33",
    fontSize: 14,
    cursor: disabled ? "default" : "pointer",
  };
}

interface ShellProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
}

/** Card frame shared by the REVIEW and SIGN screens. */
function Shell({ title, subtitle, children }: ShellProps) {
  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f5f0eb", padding: "24px 16px" }}>
      <div
        style={{
          maxWidth: 820,
          margin: "0 auto",
          backgroundColor: "white",
          borderRadius: 12,
          border: "1px solid #e6dccb",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
          padding: 24,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
          <ParseoLogo size={32} />
          <h2 style={{ color: "#182a33", margin: 0, fontSize: 20, lineHeight: 1.3 }}>
            {title}
          </h2>
        </div>
        {subtitle && (
          <p style={{ color: "#5b6e7c", margin: "0 0 20px", fontSize: 14 }}>{subtitle}</p>
        )}
        {children}
      </div>
    </div>
  );
}

function centered(content: React.ReactNode) {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f5f0eb",
        padding: 24,
        textAlign: "center",
      }}
    >
      {content}
    </div>
  );
}
